/**
 * sirus/ActionBar — R232q20 Phase 2.
 *
 * The 1:1 Sirus 3.3.5a / WoW WotLK 3.3.5a main bottom-center hotbar:
 *   – 12 slots in one row (keys 1..9, 0, -, =).
 *   – XP bar above the slot row (purple, full-width).
 *   – Reputation bar below the XP bar (gold, slim, faction-aware).
 *   – Vertical 1..6 page-switcher to the right of the slot row.
 *
 * State sources (see imports):
 *   useHotbar       — slot bindings per class (12-cell row).
 *   useKeybinds     — per-slot keybind label (defaults 1..0,-,=).
 *   useAbilityCD    — per-ability cooldown remaining + global cooldown.
 *   useUI           — `stage` gate so keys never fire in creator/realm-select.
 *   useNet          — self player → resource / level / xp / classId.
 *   useTarget       — current targetId for range-tint hook (TODO).
 *
 * DEFERRED (intentional TODOs the parent agent will wire later):
 *   – Real ability cast wire-up. Click currently triggers `useAbilityCD`
 *     cooldown + GCD locally and logs a `castSlot` event. Network send
 *     (`sendAbility`) lives in World.tsx; sirus/ActionBar must stay
 *     side-effect-free at the network level until World.tsx integrates it.
 *   – Real range check (currently always false; a hook reads targetId so
 *     the consumer can plug a distance fn here).
 *   – Drag-drop polish (only the basic abilityId payload is supported; the
 *     legacy fromSlot swap exists but no visual ghost while dragging).
 *   – Mobile swipe gesture for page switching. Page switcher is hidden on
 *     touch (`@media (pointer:coarse)`) but no swipe handler yet.
 *   – Multi-page persistence — `activePage` is local React state for now;
 *     hotbar-store still keys rows by classId (one row per class).
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { abilityById, type Ability, type ClassId } from '../../data/classes.js';
import {
  HOTBAR_DND_MIME,
  HOTBAR_SIZE,
  useHotbar,
} from '../../state/hotbar-store.js';
import { useKeybinds, eventToKey } from '../../state/keybinds-store.js';
import { useAbilityCD } from '../../state/ability-cd-store.js';
import { useUI } from '../../state/store.js';
import { useNet, useSelfPlayer } from '../../state/net-store.js';
import { useTarget } from '../../state/target-store.js';

/** WoW WotLK 3.3.5a main-bar key labels — index aligns with hotbar slot index. */
const SLOT_KEYS: readonly string[] = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=',
];

// R232q84: SLOT_PX kept only as a fallback constant for legacy paths; the
// real visual size is `var(--aiwari-slot)` (clamp 28..44 px via vmin). The
// row width is now computed from the same CSS var via calc() so the XP /
// reputation bars stretch to exactly match the slot row at every viewport.
const SLOT_PX = 36; // legacy reference only — DO NOT use for rendering size
void SLOT_PX;
const PAGE_COUNT = 6;

/** Bar-row width — computed from the slot CSS var so it matches at every viewport.
 *  Width = 12*slot + 11*gap. Used for XP & reputation bars to align edges. */
const BAR_ROW_WIDTH_CSS =
  `calc(${HOTBAR_SIZE} * var(--aiwari-slot) + ${HOTBAR_SIZE - 1} * var(--aiwari-gap))`;

type DragPayload = { abilityId?: string; fromSlot?: number };

/** XP-per-level — placeholder curve until server replicates `xpMax`. Roughly
 *  WotLK 3.3.5a's slow ramp: 400 + 200 * (level - 1) up to a soft cap at 80.
 *  Replace with the real formula once `xpMax` lands on RemotePlayer. */
function xpForLevel(level: number): number {
  const lvl = Math.max(1, Math.min(80, level || 1));
  return 400 + 200 * (lvl - 1);
}

function ActionBarImpl() {
  // Subscribe to enough store slices that React re-renders on relevant changes.
  // We deliberately don't pull `cooldowns` directly — they tick continuously
  // and would re-render at storage frequency. Instead we use a local 100ms
  // tick (matches CastBar / GCD spinner). 100ms is the smallest visually
  // perceptible step on a 1.5s GCD ring.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xffff), 100);
    return () => window.clearInterval(id);
  }, []);

  const stage = useUI((s) => s.stage);
  const selfId = useNet((s) => s.selfId);
  // R232q86zdb (Wave 2 #1): replaced the whole-`players` subscription with
  // `useSelfPlayer()` — ActionBar only ever reads self.classId / level / xp
  // / resource, so it should not re-render on every remote player movement.
  const self = useSelfPlayer();
  const targetId = useTarget((s) => s.targetId);
  const bindings = useHotbar((s) => s.bindings);
  const ensureRow = useHotbar((s) => s.ensureRow);
  const bind = useHotbar((s) => s.bind);
  const clear = useHotbar((s) => s.clear);
  const swap = useHotbar((s) => s.swap);
  const resolveKey = useKeybinds((s) => s.resolveKey);
  // Subscribing to `bindings` from useKeybinds isn't strictly required (we
  // only call resolveKey at render); but keep a dependency on the map so
  // re-keybinds in Settings live-refresh slot labels.
  const keybinds = useKeybinds((s) => s.bindings);
  void keybinds;

  void selfId; // kept for keybinds gate; the actual player row comes from useSelfPlayer above
  const classId = (self?.classId ?? null) as ClassId | null;
  const level = self?.level ?? 1;
  const xp = self?.xp ?? 0;
  const xpMax = xpForLevel(level);
  // Resource (mana / rage / focus / runic / energy) — used for OOM tint.
  const resource = self?.resource ?? 0;
  // R232q5b1: page state lives locally for now. Persisting across reloads
  // belongs in hotbar-store once we add multi-row support.
  const [activePage, setActivePage] = useState(0);
  void activePage; // page swap currently shows the same row — TODO multi-row

  // Lazy-init the per-class row so slot 0..N-1 has the level-1 starter spells.
  useEffect(() => {
    if (!classId) return;
    ensureRow(classId);
  }, [classId, ensureRow]);

  const row = useMemo(() => {
    if (!classId) return new Array<string | null>(HOTBAR_SIZE).fill(null);
    return bindings[classId] ?? new Array<string | null>(HOTBAR_SIZE).fill(null);
  }, [classId, bindings]);

  /* ─────────────── per-slot click handler ─────────────── */

  const castSlot = useCallback((slotIdx: number) => {
    if (!classId) return;
    const abilityId = row[slotIdx];
    if (!abilityId) return;
    const a = abilityById(classId, abilityId);
    if (!a) return;
    const cd = useAbilityCD.getState();
    // Local cooldown / GCD gate — server is still authoritative for actual cast.
    if (cd.remaining(a.id) > 0) return;
    if (cd.gcdRemaining() > 0) return;
    // Optimistic: fire CD + GCD client-side. Real send goes through World.tsx
    // when it integrates this component (TODO).
    cd.startCooldown(a.id, a.cooldown * 1000);
    cd.triggerGcd((self as { hastePct?: number } | null)?.hastePct ?? 0);
    if (a.windup > 0) {
      cd.startCast(a.id, a.name.ru, a.windup * 1000);
      window.setTimeout(() => useAbilityCD.getState().clearCast(), a.windup * 1000);
    } else {
      cd.pulseInstant(a.id, a.name.ru);
    }
    // Bridge to World.tsx via global hook if defined; otherwise log.
    const w = window as unknown as { __aiwariCastSlot?: (slot: number, id: string) => void };
    if (typeof w.__aiwariCastSlot === 'function') {
      try { w.__aiwariCastSlot(slotIdx, abilityId); } catch { /* noop */ }
    } else {
      // eslint-disable-next-line no-console
      console.debug('[sirus/ActionBar] castSlot', slotIdx, abilityId);
    }
  }, [classId, row, self]);

  /* ─────────────── keyboard handler — only when stage === 'world' ─────────────── */

  // Keep `castSlot` reference fresh inside the global listener without
  // re-binding on every render (otherwise we'd thrash the listener at 10Hz
  // due to the tick state).
  const castSlotRef = useRef(castSlot);
  castSlotRef.current = castSlot;

  useEffect(() => {
    if (stage !== 'world') return;
    const handler = (e: KeyboardEvent) => {
      // Skip if the user is typing into chat / search / a form input.
      const tgt = e.target as HTMLElement | null;
      if (tgt) {
        const tag = tgt.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt.isContentEditable) return;
      }
      const k = eventToKey(e);
      // Walk the 12 slots; check if this canonical key matches the slot's
      // resolved keybind. We support the per-bar override via keybinds-store
      // (`hotbar.bar1.<n>`); fallback to the literal SLOT_KEYS label.
      for (let i = 0; i < HOTBAR_SIZE; i++) {
        const bound = (resolveKey(`hotbar.bar1.${i + 1}`) || SLOT_KEYS[i]).toUpperCase();
        if (k === bound) {
          e.preventDefault();
          castSlotRef.current(i);
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [stage, resolveKey]);

  /* ─────────────── render ─────────────── */

  if (stage !== 'world') return null;

  const cd = useAbilityCD.getState();
  const gcdRem = cd.gcdRemaining();
  const gcdActive = gcdRem > 0;

  return (
    <div
      className="aiwari-action-bar"
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        pointerEvents: 'auto',
        fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
        color: 'var(--aiwari-fg, #E8E1DA)',
      }}
      data-mobile-shrink="true"
    >
      {/* ── XP bar ─────────────────────────────────────────────── */}
      <XpBar level={level} xp={xp} xpMax={xpMax} width={BAR_ROW_WIDTH_CSS} />

      {/* ── Reputation bar ─────────────────────────────────────── */}
      <ReputationBar width={BAR_ROW_WIDTH_CSS} />

      {/* Strip wrapping slots + page switcher */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--aiwari-gap)', marginTop: 2 }}>
        {/* Genuine 3.3.5 end-cap gryphons flanking the bar. */}
        <div aria-hidden style={{
          position: 'absolute', left: 'calc(-1 * var(--aiwari-slot) * 1.9)', bottom: 'calc(-1 * var(--aiwari-slot) * 0.28)',
          width: 'calc(var(--aiwari-slot) * 2.1)', height: 'calc(var(--aiwari-slot) * 2.1)',
          backgroundImage: `url("${import.meta.env.BASE_URL}ui-sirus/Interface/MAINMENUBAR/UI-MainMenuBar-EndCap-Human.png")`,
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom left',
          pointerEvents: 'none', zIndex: 1,
        }} />
        <div aria-hidden style={{
          position: 'absolute', right: 'calc(-1 * var(--aiwari-slot) * 1.9)', bottom: 'calc(-1 * var(--aiwari-slot) * 0.28)',
          width: 'calc(var(--aiwari-slot) * 2.1)', height: 'calc(var(--aiwari-slot) * 2.1)',
          backgroundImage: `url("${import.meta.env.BASE_URL}ui-sirus/Interface/MAINMENUBAR/UI-MainMenuBar-EndCap-Human.png")`,
          backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom right',
          transform: 'scaleX(-1)',
          pointerEvents: 'none', zIndex: 1,
        }} />
        {/* ── Slot row ─────────────────────────────────────────── */}
        <div
          className="aiwari-action-bar__row"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${HOTBAR_SIZE}, var(--aiwari-slot))`,
            gap: 'var(--aiwari-gap)',
          }}
        >
          {SLOT_KEYS.map((defaultKey, i) => {
            const ability = classId && row[i]
              ? abilityById(classId, row[i] as string) ?? null
              : null;
            const keyLabel = (resolveKey(`hotbar.bar1.${i + 1}`) || defaultKey).replace(/^.*\+/, '');
            return (
              <Slot
                key={`slot-${i}`}
                idx={i}
                keyLabel={keyLabel}
                ability={ability}
                onClick={() => castSlot(i)}
                onClear={() => classId && clear(classId, i)}
                onDrop={(payload) => {
                  if (!classId) return;
                  const fromSlot = typeof payload.fromSlot === 'number' ? payload.fromSlot : -1;
                  if (!payload.abilityId) return;
                  if (fromSlot >= 0 && fromSlot < HOTBAR_SIZE) {
                    swap(classId, fromSlot, i);
                  } else {
                    bind(classId, i, payload.abilityId);
                  }
                }}
                gcdActive={gcdActive}
                gcdRem={gcdRem}
                resource={resource}
                targetId={targetId}
              />
            );
          })}
        </div>

        {/* R232q86zg8: page switcher hidden — retail 3.3.5 keeps pages on
            Shift+1..6, the visible 1-6 column was non-canon (user report).
            Multi-row support returns with real bottom-left/right bars. */}
        {false && <PageSwitcher activePage={activePage} onChange={setActivePage} />}
      </div>

      {/* Mobile: shrink the whole bar to 0.85, hide page switcher. */}
      <style>{`
        @media (pointer: coarse) {
          .aiwari-action-bar[data-mobile-shrink='true'] {
            transform: translateX(-50%) scale(0.85);
            transform-origin: bottom center;
          }
          .aiwari-action-bar[data-mobile-shrink='true'] .aiwari-action-bar__pages {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Sub-components
 * ──────────────────────────────────────────────────────────────── */

function Slot({
  idx,
  keyLabel,
  ability,
  onClick,
  onClear,
  onDrop,
  gcdActive,
  gcdRem,
  resource,
  targetId,
}: {
  idx: number;
  keyLabel: string;
  ability: Ability | null;
  onClick: () => void;
  onClear: () => void;
  onDrop: (payload: DragPayload) => void;
  gcdActive: boolean;
  gcdRem: number;
  resource: number;
  targetId: string | null;
}) {
  const cd = useAbilityCD.getState();
  const remaining = ability ? cd.remaining(ability.id) : 0;
  const onCd = remaining > 0;
  const cdSec = onCd ? Math.ceil(remaining / 100) / 10 : 0;
  const cdSweepDeg = ability && onCd && ability.cooldown > 0
    ? Math.min(360, (remaining / (ability.cooldown * 1000)) * 360)
    : 0;
  const gcdSweepDeg = gcdActive ? Math.min(360, (gcdRem / 1500) * 360) : 0;

  // OOM tint — blue overlay when not enough resource.
  const oom = ability ? resource < ability.cost : false;
  // Range tint — TODO real distance check; for now we only know whether a
  // target exists. Once World.tsx exposes a distance helper, plug in here.
  const outOfRange = false;
  void targetId; // hook reserved for the future range check

  const slotStyle: CSSProperties = {
    position: 'relative',
    width: 'var(--aiwari-slot)',
    height: 'var(--aiwari-slot)',
    padding: 0,
    // Genuine 3.3.5 quickslot frame under the icon (empty slots show it).
    backgroundColor: 'var(--aiwari-frame-stone, #1F1A14)',
    backgroundImage: `url("${import.meta.env.BASE_URL}ui-sirus/Interface/BUTTONS/UI-Quickslot.png")`,
    backgroundSize: '164% 164%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: 2,
    boxSizing: 'border-box',
    cursor: ability ? 'pointer' : 'default',
    userSelect: 'none',
  };

  const handleDragStart = (e: React.DragEvent<HTMLButtonElement>) => {
    if (!ability) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      HOTBAR_DND_MIME,
      JSON.stringify({ abilityId: ability.id, fromSlot: idx } satisfies DragPayload),
    );
  };

  const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    if (e.dataTransfer.types.includes(HOTBAR_DND_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(HOTBAR_DND_MIME);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as DragPayload;
      if (payload && typeof payload === 'object') onDrop(payload);
    } catch { /* malformed — ignore */ }
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClear();
  };

  const tooltip = ability
    ? `${ability.name.ru} — ${ability.desc.ru}\nПКМ — снять с панели`
    : 'Перетащи заклинание из книги (P)';

  return (
    <button
      type="button"
      className="aiwari-icon-slot aiwari-stone-btn"
      style={slotStyle}
      title={tooltip}
      draggable={!!ability}
      onClick={onClick}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onContextMenu={handleContextMenu}
      data-disabled={!ability ? 'true' : undefined}
    >
      {ability ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'clamp(16px, 2.4vmin, 28px)',
            lineHeight: 1,
            // Custom icon image goes here once art lands. For now the emoji
            // glyph from `Ability.icon` doubles as placeholder art.
            filter: onCd ? 'grayscale(0.85) brightness(0.55)' : undefined,
          }}
        >
          {ability.icon}
        </span>
      ) : (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--aiwari-text-md)',
            color: 'var(--aiwari-fg-dim, hsl(34 14% 35%))',
            fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
            pointerEvents: 'none',
          }}
        >◇</span>
      )}

      {/* Cooldown sweep — clockwise conic-gradient, fades as remaining drops. */}
      {onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `conic-gradient(rgba(0,0,0,0.62) 0deg ${cdSweepDeg}deg, transparent ${cdSweepDeg}deg 360deg)`,
            pointerEvents: 'none',
            transition: 'background 80ms linear',
          }}
        />
      )}
      {/* Cooldown numeric — shown when >= 1.0s remains. */}
      {onCd && remaining >= 1000 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--aiwari-gold-soft, #FFD479)',
            fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
            fontSize: 'var(--aiwari-text-md)',
            fontWeight: 700,
            textShadow: '0 1px 2px #000',
            pointerEvents: 'none',
          }}
        >
          {cdSec.toFixed(cdSec >= 10 ? 0 : 1)}
        </span>
      )}
      {/* Global cooldown — soft white pulse layered above the icon. */}
      {gcdActive && !onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `conic-gradient(rgba(255,255,255,0.16) 0deg ${gcdSweepDeg}deg, transparent ${gcdSweepDeg}deg 360deg)`,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* OOM tint — blue overlay. */}
      {oom && !onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(40, 90, 200, 0.28)',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Range tint — red overlay (deferred). */}
      {outOfRange && !onCd && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(200, 40, 40, 0.28)',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Keybind label — bottom-right corner, tiny mono font. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          right: 2,
          bottom: 1,
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 'var(--aiwari-text-sm)',
          lineHeight: 1,
          color: 'var(--aiwari-fg-muted, #B19B7E)',
          textShadow: '0 1px 0 #000',
          pointerEvents: 'none',
        }}
      >{keyLabel}</span>
    </button>
  );
}

function XpBar({
  level,
  xp,
  xpMax,
  width,
}: {
  level: number;
  xp: number;
  xpMax: number;
  width: string;
}) {
  const pct = xpMax > 0 ? Math.max(0, Math.min(100, (xp / xpMax) * 100)) : 0;
  return (
    <div
      style={{
        position: 'relative',
        width,
        height: 'var(--aiwari-bar-h)',
        background: 'hsl(0 0% 0% / 0.6)',
        border: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
        borderRadius: 2,
        overflow: 'hidden',
      }}
      title={`Уровень ${level} — ${xp}/${xpMax} опыта`}
    >
      <div
        style={{
          position: 'absolute',
          inset: '0 auto 0 0',
          width: `${pct}%`,
          height: '100%',
          background: 'var(--aiwari-bar-xp, hsl(280 60% 50%))',
          transition: 'width 160ms linear',
        }}
      />
      <span
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'block',
          width: '100%',
          textAlign: 'center',
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 'var(--aiwari-text-sm)',
          lineHeight: 'var(--aiwari-bar-h)',
          color: 'var(--aiwari-fg, #E8E1DA)',
          textShadow: '0 1px 0 #000',
          letterSpacing: 0.4,
        }}
      >
        {`Ур. ${level} — ${xp}/${xpMax}`}
      </span>
    </div>
  );
}

function ReputationBar({ width }: { width: string }) {
  // Placeholder values until faction reputation lands on RemotePlayer.
  // Tracks "Дружелюбен" (Friendly) → "Уважение" (Honored) at 1500/3000.
  const standing = 'Дружелюбен';
  const value = 1500;
  const max = 3000;
  const pct = (value / max) * 100;
  return (
    <div
      style={{
        position: 'relative',
        width,
        // Reputation bar is a slimmer track — half the standard bar height.
        height: 'clamp(6px, 0.8vmin, 10px)',
        background: 'hsl(0 0% 0% / 0.6)',
        border: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
        borderRadius: 2,
        overflow: 'hidden',
      }}
      title={`Репутация — ${standing} (${value}/${max})`}
    >
      <div
        style={{
          position: 'absolute',
          inset: '0 auto 0 0',
          width: `${pct}%`,
          height: '100%',
          background: 'var(--aiwari-gold, hsl(44 100% 49%))',
          transition: 'width 160ms linear',
        }}
      />
    </div>
  );
}

function PageSwitcher({
  activePage,
  onChange,
}: {
  activePage: number;
  onChange: (n: number) => void;
}) {
  return (
    <div
      className="aiwari-action-bar__pages"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        marginLeft: 4,
      }}
    >
      {Array.from({ length: PAGE_COUNT }, (_, i) => {
        const active = i === activePage;
        return (
          <button
            key={`page-${i + 1}`}
            type="button"
            onClick={() => onChange(i)}
            style={{
              width: 'clamp(14px, 2vmin, 22px)',
              height: 'clamp(11px, 1.5vmin, 16px)',
              padding: 0,
              fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
              fontSize: 'var(--aiwari-text-sm)',
              lineHeight: 1,
              color: active
                ? 'var(--aiwari-gold-soft, #FFD479)'
                : 'var(--aiwari-fg-muted, #B19B7E)',
              background: active
                ? 'hsl(44 30% 18%)'
                : 'hsl(0 0% 0% / 0.55)',
              border: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
              borderRadius: 2,
              cursor: 'pointer',
            }}
            title={`Панель ${i + 1}`}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}

/**
 * R232q86zdb (Wave 2 #1): React.memo wrapper.
 *
 * ActionBar receives no props from its parent — all reactivity comes from
 * its zustand subscriptions. With `useSelfPlayer()` in place (above) it
 * only re-renders when the self player's row changes. The memo ensures a
 * pure parent re-render (e.g. SirusHud) never triggers a fresh hotbar
 * render with the same effective state.
 */
export const ActionBar = memo(ActionBarImpl);
export default ActionBar;
